import { Component, EventBus } from '../core/smpl-components'
import { Events } from '../core/events'
import { StickyHeader } from '../modules/sticky-header.js'
import { gsap } from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin.js'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger)

/** Anchor nav
 *
 * Button row at the top of the page linking to the anchor components.
 * - StickyRow:    hides the (position: sticky) row on scroll down, shows it on scroll up.
 *                 Mobile only: on desktop the CSS keeps it in view.
 * - AnchorScroll: scrolls to the anchor with GSAP instead of jumping.
 * - ActiveLink:   marks the button of the section that has passed the viewport middle.
 * - Reveal:       the buttons bounce in one after the other, on the menu's spring.
 */

// 992px is `l` in _tokens/_breakpoints.scss.
const desktopQuery = window.matchMedia('(min-width: 992px)')

// The anchor itself is taken out of the grid flow (_anchor.scss), so its own
// position is not reliable: use the component that follows it.
function getSection(link) {
	const anchor = document.getElementById(decodeURIComponent(link.getAttribute('href').slice(1)))
	return anchor ? (anchor.nextElementSibling ?? anchor) : null
}

// -- Sticky Row ---------------------------------------------------- //
// The row sits in the flow (pushing the intro down, wrapping on mobile) and is
// position: sticky. StickyHeader may only hide it once it is actually stuck,
// so its offset is the scroll position where sticking starts.
class StickyRow {
	constructor(el) {
		this.el = el

		this.bndOnResize = this.onResize.bind(this)
		EventBus.on(Events.PAGE_RESIZE, this.bndOnResize)

		this.stickyHeader = new StickyHeader({
			element: this.el,
			offset: this.getStickOffset(),
			showAtBottom: false,
		})
	}
	// Measured from the previous sibling (or parent), which is in normal flow,
	// so it is correct at any scroll position.
	getStickOffset() {
		const prev = this.el.previousElementSibling
		const flowTop = prev ? prev.getBoundingClientRect().bottom : this.el.parentElement.getBoundingClientRect().top
		const style = getComputedStyle(this.el)

		return flowTop + window.scrollY + parseFloat(style.marginTop) - parseFloat(style.top)
	}
	// Space the row covers when it is visible at the top of the viewport.
	get coveredHeight() {
		return this.el.offsetHeight + parseFloat(getComputedStyle(this.el).top)
	}
	onResize() {
		this.stickyHeader.options.offset = this.getStickOffset()
		this.stickyHeader.refresh()
	}
	destroy() {
		EventBus.off(Events.PAGE_RESIZE, this.bndOnResize)
		this.stickyHeader.destroy()
	}
}

// -- Anchor Scroll ------------------------------------------------- //
// Scrolls to the component that follows the anchor (see getSection).
class AnchorScroll {
	static GAP = 20

	constructor(el, stickyRow) {
		this.el = el
		this.stickyRow = stickyRow

		this.bndOnClick = this.onClick.bind(this)
		this.el.addEventListener('click', this.bndOnClick)
	}
	onClick(e) {
		const link = e.target.closest('a[href^="#"]')
		if (!link) return

		const section = getSection(link)
		if (!section) return

		e.preventDefault()
		e.stopPropagation()

		this.scrollTo(section)
		history.replaceState(history.state, '', link.getAttribute('href'))
	}
	scrollTo(target) {
		// The row is visible on desktop, and on mobile when scrolling up brings
		// it back, so keep the target clear of it then.
		const isUp = target.getBoundingClientRect().top < 0
		const rowVisible = isUp || desktopQuery.matches
		const offsetY = AnchorScroll.GAP + (rowVisible ? this.stickyRow.coveredHeight : 0)

		this.tween?.kill()
		this.tween = gsap.to(window, {
			duration: 0.8,
			scrollTo: { y: target, offsetY },
			ease: 'power4.out',
		})
	}
	destroy() {
		this.el.removeEventListener('click', this.bndOnClick)
		this.tween?.kill()
	}
}

// -- Active Link --------------------------------------------------- //
// A section is active from the moment its top passes the viewport middle until
// the next section's top does (the last one stays active to the page end).
class ActiveLink {
	constructor(el) {
		const items = [...el.querySelectorAll('a[href^="#"]')]
			.map((link) => ({ link, section: getSection(link) }))
			.filter((item) => item.section)

		this.triggers = items.map(({ link, section }, i) => {
			const next = items[i + 1]?.section

			return ScrollTrigger.create({
				trigger: section,
				start: 'top center',
				endTrigger: next ?? section,
				end: next ? 'top center' : 'max',
				onToggle: ({ isActive }) => {
					link.classList.toggle('is-active', isActive)
					link.toggleAttribute('aria-current', isActive)
				},
			})
		})
	}
	destroy() {
		this.triggers.forEach((trigger) => trigger.kill())
	}
}

// -- Reveal -------------------------------------------------------- //
// Same spring and stagger as the menu items (main-nav.js), so the two read as
// one family. The list items move, not the buttons, which keep their own hover
// transitions.
function reveal(el) {
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null

	return gsap.fromTo(
		el.querySelectorAll('.anchor-nav__list > li'),
		// From above (the menu's items rise from below): they drop into place.
		{ autoAlpha: 0, yPercent: -30 },
		{
			autoAlpha: 1,
			yPercent: 0,
			duration: 0.7,
			ease: 'elastic.out(2,0.75)',
			stagger: 0.06,
			// Lets the page settle (the page transition, the intro) before they come.
			delay: 0.5,
			clearProps: 'opacity,visibility,transform',
		},
	)
}

// -------------------------------------------------------------------------- //
export default class extends Component {
	prepare() {}
	mount() {
		this.stickyRow = new StickyRow(this.element)
		this.anchorScroll = new AnchorScroll(this.element, this.stickyRow)
		this.activeLink = new ActiveLink(this.element)
		this.revealTween = reveal(this.element)
	}
	unmount() {
		this.revealTween?.kill()
		this.revealTween = null

		if (this.activeLink) {
			this.activeLink.destroy()
			this.activeLink = null
		}

		if (this.anchorScroll) {
			this.anchorScroll.destroy()
			this.anchorScroll = null
		}

		if (this.stickyRow) {
			this.stickyRow.destroy()
			this.stickyRow = null
		}
	}
}
